// File: /var/www/app2/frontend/src/utils/generateReportPDF.ts

import jsPDF from "jspdf";

interface Report {
  missionary_name: string;
  missionary_number: string | number;
  state: string;
  location: string;
  month: number;
  year: number;
  status: string;
  submission_date: string | Date;
}

interface ReportData {
  heardGospel?: number;
  receivedJesus?: number;
  baptized?: number;
  discoveryBibleStudyStarted?: number;
  churchesPlanted?: number;
  leadersTrained?: number;
  testimony?: string;
  challenges?: string;
  prayerRequests?: string;
  pictures?: Picture[] | string[];
}

interface Picture {
  path: string;
  description?: string;
}

interface ImageResult {
  success: boolean;
  dataUrl?: string;
  width?: number;
  height?: number;
}

interface LoadedPicture extends Picture {
  imageData: ImageResult;
}

interface Stat {
  label: string;
  value: number;
}

// A single planted church from the field app's Church Planting Details
// prompt (village, GPS location, believers count) — behind the plain
// "Churches Planted" count in reportData.churchesPlanted above.
interface ChurchPlant {
  id: number;
  village_name: string | null;
  people_group: string | null;
  church_name: string;
  start_month: number | null;
  start_year: number | null;
  believers_count: number;
  gps_lat: number | null;
  gps_lng: number | null;
}

export const generateReportPDF = async (
  report: Report,
  reportData: ReportData,
  churchPlants: ChurchPlant[] = []
): Promise<void> => {
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let currentY = 20;

  // Helper function to add text with word wrap
  const addText = (
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    fontSize: number = 12,
    fontStyle: "normal" | "bold" | "italic" | "bolditalic" = "normal"
  ): number => {
    pdf.setFontSize(fontSize);
    pdf.setFont("helvetica", fontStyle);
    const lines = pdf.splitTextToSize(text || "", maxWidth);
    pdf.text(lines, x, y);
    return lines.length * (fontSize * 0.4); // Return height used
  };

  // Helper function to check if we need a new page
  const checkNewPage = (neededHeight: number): boolean => {
    if (currentY + neededHeight > pageHeight - 20) {
      pdf.addPage();
      currentY = 20;
      return true;
    }
    return false;
  };

  // Helper function to load image as data URL
  const loadImageAsDataURL = (imageUrl: string): Promise<ImageResult> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve({ success: false });
            return;
          }
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
          resolve({
            dataUrl: dataUrl,
            width: img.width,
            height: img.height,
            success: true,
          });
        } catch (error) {
          console.error("Error converting image to data URL:", error);
          resolve({ success: false });
        }
      };

      img.onerror = (error) => {
        console.error("Error loading image:", error);
        resolve({ success: false });
      };

      // Set timeout for image loading
      setTimeout(() => {
        resolve({ success: false });
      }, 5000); // 5 second timeout

      img.src = imageUrl;
    });
  };

  // Title
  pdf.setFontSize(20);
  pdf.setFont("helvetica", "bold");
  pdf.text("Monthly Ministry Report", pageWidth / 2, currentY, {
    align: "center",
  });
  currentY += 15;

  // Report Information Section
  pdf.setFillColor(240, 240, 240);
  pdf.rect(10, currentY, pageWidth - 20, 40, "F");
  currentY += 5;

  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.text("Report Information", 15, currentY);
  currentY += 8;

  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  pdf.text(
    `Missionary: ${report.missionary_name} (${report.missionary_number})`,
    15,
    currentY
  );
  currentY += 6;
  pdf.text(`Location: ${report.state}, ${report.location}`, 15, currentY);
  currentY += 6;
  pdf.text(
    `Period: ${getMonthName(report.month)} ${report.year}`,
    15,
    currentY
  );
  currentY += 6;
  pdf.text(`Status: ${report.status.toUpperCase()}`, 15, currentY);
  currentY += 6;
  pdf.text(
    `Submitted: ${new Date(report.submission_date).toLocaleDateString()}`,
    15,
    currentY
  );
  currentY += 15;

  // Ministry Statistics Section
  checkNewPage(50);
  pdf.setFillColor(240, 240, 240);
  pdf.rect(10, currentY, pageWidth - 20, 8, "F");
  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.text("Ministry Statistics", 15, currentY + 6);
  currentY += 15;

  // Statistics in two columns
  const stats: Stat[] = [
    { label: "People Who Heard Gospel:", value: reportData.heardGospel || 0 },
    {
      label: "People Who Received Jesus:",
      value: reportData.receivedJesus || 0,
    },
    { label: "People Baptized:", value: reportData.baptized || 0 },
    {
      label: "Discovery Bible Studies Started:",
      value: reportData.discoveryBibleStudyStarted || 0,
    },
    { label: "Churches Planted:", value: reportData.churchesPlanted || 0 },
    { label: "Leaders Trained:", value: reportData.leadersTrained || 0 },
  ];

  pdf.setFontSize(11);
  stats.forEach((stat, index) => {
    const x = index % 2 === 0 ? 15 : 110;
    const y = currentY + Math.floor(index / 2) * 7;
    pdf.setFont("helvetica", "normal");
    pdf.text(stat.label, x, y);
    pdf.setFont("helvetica", "bold");
    pdf.text(stat.value.toString(), x + 60, y);
  });
  currentY += 25;

  // Church Planting Detail Section — per-church breakdown behind the plain
  // count above, including each church's GPS location.
  if (churchPlants.length > 0) {
    checkNewPage(20);
    pdf.setFillColor(240, 240, 240);
    pdf.rect(10, currentY, pageWidth - 20, 8, "F");
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text(`Churches Planted — Detail (${churchPlants.length})`, 15, currentY + 6);
    currentY += 15;

    for (let i = 0; i < churchPlants.length; i++) {
      const c = churchPlants[i];
      checkNewPage(24);

      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.text(c.church_name, 15, currentY);
      currentY += 6;

      pdf.setFont("helvetica", "normal");
      if (c.village_name) {
        pdf.text(`Village: ${c.village_name}`, 15, currentY);
        currentY += 5;
      }
      if (c.people_group) {
        pdf.text(`People group: ${c.people_group}`, 15, currentY);
        currentY += 5;
      }
      let believersLine = `Believers: ${c.believers_count}`;
      if (c.start_month && c.start_year) {
        believersLine += ` — Started ${getMonthName(c.start_month)} ${c.start_year}`;
      }
      pdf.text(believersLine, 15, currentY);
      currentY += 5;

      pdf.text(
        c.gps_lat != null && c.gps_lng != null
          ? `Location: ${c.gps_lat.toFixed(6)}, ${c.gps_lng.toFixed(6)}`
          : "No GPS location recorded",
        15,
        currentY
      );
      currentY += 8;

      if (i < churchPlants.length - 1) {
        pdf.setDrawColor(220, 220, 220);
        pdf.line(15, currentY - 2, pageWidth - 15, currentY - 2);
        currentY += 4;
      }
    }
    currentY += 10;
  }

  // Testimony Section
  checkNewPage(30);
  pdf.setFillColor(240, 240, 240);
  pdf.rect(10, currentY, pageWidth - 20, 8, "F");
  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.text("Testimony", 15, currentY + 6);
  currentY += 12;

  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  const testimonyHeight = addText(
    reportData.testimony || "No testimony provided",
    15,
    currentY,
    pageWidth - 30
  );
  currentY += testimonyHeight + 10;

  // Challenges Section
  checkNewPage(30);
  pdf.setFillColor(240, 240, 240);
  pdf.rect(10, currentY, pageWidth - 20, 8, "F");
  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.text("Challenges", 15, currentY + 6);
  currentY += 12;

  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  const challengesHeight = addText(
    reportData.challenges || "No challenges reported",
    15,
    currentY,
    pageWidth - 30
  );
  currentY += challengesHeight + 10;

  // Prayer Requests Section
  checkNewPage(30);
  pdf.setFillColor(240, 240, 240);
  pdf.rect(10, currentY, pageWidth - 20, 8, "F");
  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.text("Prayer Requests", 15, currentY + 6);
  currentY += 12;

  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  const prayerHeight = addText(
    reportData.prayerRequests || "No prayer requests",
    15,
    currentY,
    pageWidth - 30
  );
  currentY += prayerHeight + 10;

  // Pictures Section with actual images
  if (reportData.pictures && reportData.pictures.length > 0) {
    // Start pictures on a new page
    pdf.addPage();
    currentY = 20;

    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text("Pictures", pageWidth / 2, currentY, { align: "center" });
    currentY += 15;

    // Process pictures
    const pictures = processPicturesForPDF(reportData.pictures);

    // Show loading message
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text("Loading images...", pageWidth / 2, currentY, { align: "center" });
    pdf.setTextColor(0, 0, 0);

    // Load all images first
    const imagePromises = pictures.map(async (pic): Promise<LoadedPicture> => {
      const imagePath = pic.path.replace("/uploads/", "");
      const imageUrl = `/api/image-proxy/${imagePath}`;
      const result = await loadImageAsDataURL(imageUrl);
      return { ...pic, imageData: result };
    });

    const loadedPictures = await Promise.all(imagePromises);

    // Clear loading message by adding a white rectangle over it
    pdf.setFillColor(255, 255, 255);
    pdf.rect(10, currentY - 5, pageWidth - 20, 10, "F");

    // Now add the pictures
    for (let i = 0; i < loadedPictures.length; i++) {
      const pic = loadedPictures[i];

      // Check if we need a new page for this picture
      if (currentY + 120 > pageHeight - 20) {
        pdf.addPage();
        currentY = 20;
      }

      // Picture number and description
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text(`Picture ${i + 1}`, 15, currentY);
      currentY += 6;

      pdf.setFontSize(11);
      pdf.setFont("helvetica", "normal");
      const descHeight = addText(
        pic.description || "No description provided",
        15,
        currentY,
        pageWidth - 30
      );
      currentY += descHeight + 5;

      // Add the image or placeholder
      if (pic.imageData && pic.imageData.success) {
        try {
          // Calculate dimensions to fit the image
          const maxWidth = pageWidth - 30;
          const maxHeight = 100;
          let width = pic.imageData.width!;
          let height = pic.imageData.height!;

          // Scale down if needed
          const widthRatio = maxWidth / width;
          const heightRatio = maxHeight / height;
          const ratio = Math.min(widthRatio, heightRatio, 1); // Don't scale up

          width = width * ratio;
          height = height * ratio;

          // Center the image horizontally
          const xOffset = (pageWidth - width) / 2;

          // Add the image
          pdf.addImage(
            pic.imageData.dataUrl!,
            "JPEG",
            xOffset,
            currentY,
            width,
            height
          );
          currentY += height + 10;
        } catch (error) {
          console.error("Error adding image to PDF:", error);
          // Add placeholder if image fails
          addImagePlaceholder(pdf, currentY, pageWidth, "Error loading image");
          currentY += 85;
        }
      } else {
        // Add placeholder if image couldn't be loaded
        addImagePlaceholder(
          pdf,
          currentY,
          pageWidth,
          "Image could not be loaded"
        );
        currentY += 85;
      }

      // Add spacing between pictures
      if (i < loadedPictures.length - 1) {
        currentY += 5;
        // Add a separator line
        pdf.setDrawColor(220, 220, 220);
        pdf.line(15, currentY, pageWidth - 15, currentY);
        currentY += 10;
      }
    }
  }

  // Footer on all pages
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(10);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
      align: "center",
    });
    pdf.text(
      `Generated on ${new Date().toLocaleDateString()}`,
      pageWidth - 15,
      pageHeight - 10,
      { align: "right" }
    );
    pdf.setTextColor(0, 0, 0);
  }

  // Generate the PDF and open it in a new tab for viewing
  // Format: {number}_{name}_{month}_{year}.pdf
  const sanitizedName = report.missionary_name.replace(/[^a-zA-Z0-9]/g, '_');
  const fileName = `${report.missionary_number}_${sanitizedName}_${report.month}_${report.year}.pdf`;

  try {
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
  } catch (error) {
    console.error('Error generating/displaying PDF:', error);
    pdf.save(fileName);
  }
};

// Helper function to add image placeholder
const addImagePlaceholder = (pdf: jsPDF, y: number, pageWidth: number, message: string): void => {
  pdf.setDrawColor(200, 200, 200);
  pdf.setFillColor(245, 245, 245);
  pdf.rect(15, y, pageWidth - 30, 80, "FD");

  // Add icon/message in center
  pdf.setFontSize(10);
  pdf.setTextColor(150, 150, 150);

  // Draw a simple image icon
  const centerX = pageWidth / 2;
  const centerY = y + 40;

  // Draw mountain icon (simple triangles)
  pdf.setDrawColor(180, 180, 180);
  pdf.line(centerX - 15, centerY + 5, centerX - 5, centerY - 5);
  pdf.line(centerX - 5, centerY - 5, centerX + 5, centerY + 5);
  pdf.line(centerX - 5, centerY + 5, centerX + 5, centerY - 10);
  pdf.line(centerX + 5, centerY - 10, centerX + 15, centerY + 5);

  // Draw sun (circle)
  pdf.circle(centerX + 10, centerY - 10, 3);

  // Add message below
  pdf.text(message, centerX, centerY + 15, { align: "center" });
  pdf.setTextColor(0, 0, 0);
  pdf.setDrawColor(0, 0, 0);
};

// Helper function to get month name
const getMonthName = (monthNumber: number): string => {
  const months = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[monthNumber] || "";
};

// Helper function to process pictures
const processPicturesForPDF = (pictures: Picture[] | string[]): Picture[] => {
  if (!pictures) return [];

  if (pictures.length > 0) {
    if (typeof pictures[0] === "object" && (pictures[0] as Picture).path) {
      return pictures as Picture[];
    } else if (typeof pictures[0] === "string") {
      return (pictures as string[]).map((path, index) => ({
        path: path,
        description: `Picture ${index + 1}`,
      }));
    }
  }

  return [];
};

export default generateReportPDF;
